// ─── Vacances & jours fériés — gestion (indépendante des gardes) ──
// Deux sections repliables :
//   • Vacances scolaires : zone + année, import gouv (⬇️, sans doublon), liste.
//   • Jours fériés : import France par année (⬇️), ajout personnalisé, liste.
// Config stockée sur calendriers/{calId}.vacancesConfig : { periodes[], feries[] }.
// La garde durant les vacances (½1/½2) se règle dans « Gardes ».
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { useUI, useT, Feuille, SectionPliable, SelecteurDate, LigneSwipe, confirmerSuppression, frJMA } from '@mop/core';
import {
  ZONES, getAnneesDisponibles, emojiVacances, chargerVacancesGouvernement,
  sauvegarderVacancesConfig, chargerVacancesConfig,
} from './services/vacances';
import { joursFeriesFr } from './services/joursFeries';
import FeuilleFichePeriode from './FeuilleFichePeriode';

const cle = (p) => `${p.nom}|${p.debut}|${p.fin}`;
const fmtFR = frJMA;   // JJ/MM/AAAA (Core)

export default function FeuilleVacances({ visible, calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const annees = useMemo(() => getAnneesDisponibles(), []);
  const anCourant = new Date().getFullYear();
  const anneesFerie = [anCourant - 1, anCourant, anCourant + 1, anCourant + 2];

  const [zone, setZone] = useState('Zone B');
  const [annee, setAnnee] = useState(annees[1]);
  const [periodes, setPeriodes] = useState([]);
  const [feries, setFeries] = useState([]);        // [{ nom, date }]
  const [anFerie, setAnFerie] = useState(anCourant);
  const [nomFerieNew, setNomFerieNew] = useState('');
  const [dateFerieNew, setDateFerieNew] = useState('');
  const [pickFerie, setPickFerie] = useState(false);
  const [ouvert, setOuvert] = useState('vac');
  const [busy, setBusy] = useState(false);
  const [modifie, setModifie] = useState(false);
  const [msg, setMsg] = useState('');
  const [fiche, setFiche] = useState(-1);

  useEffect(() => {
    if (!visible || !calId) return;
    chargerVacancesConfig(calId).then(cfg => {
      if (cfg?.zone) setZone(cfg.zone);
      if (cfg?.annee) setAnnee(cfg.annee);
      if (Array.isArray(cfg?.periodes)) setPeriodes(cfg.periodes);
      if (Array.isArray(cfg?.feries)) setFeries(cfg.feries);
      setModifie(false); setMsg('');
    }).catch(() => {});
  }, [visible, calId]);

  // ── Vacances scolaires ──
  async function chargerVac() {
    setBusy(true); setMsg('');
    try {
      const recues = await chargerVacancesGouvernement(zone, annee);
      const existantes = new Set(periodes.map(cle));
      const nouvelles = recues.filter(r => !existantes.has(cle(r))).map(r => ({ ...r, actif: true }));
      if (nouvelles.length === 0) setMsg(t('vacances.aucuneNouvellePeriode'));
      else { setPeriodes(prev => [...prev, ...nouvelles].sort((a, b) => a.debut.localeCompare(b.debut))); setModifie(true); setMsg(t('vacances.periodesAjoutees', { n: nouvelles.length })); }
    } catch (e) { setMsg(e?.message || t('vacances.chargementImpossible')); }
    setBusy(false);
  }
  function supprimerVac(k, nom) {
    confirmerSuppression(t('vacances.supprimerTitre'), t('vacances.periodeRetireeMsg', { nom }), () => {
      setPeriodes(ps => ps.filter(p => cle(p) !== k)); setModifie(true);
    });
  }

  // ── Jours fériés ──
  function importerFeries() {
    const map = joursFeriesFr(anFerie);
    const existantes = new Set(feries.map(f => f.date));
    const nouv = Object.entries(map).filter(([date]) => !existantes.has(date)).map(([date, nom]) => ({ date, nom }));
    if (nouv.length === 0) { setMsg(t('vacances.aucunNouveauFerie', { annee: anFerie })); return; }
    setFeries(prev => [...prev, ...nouv].sort((a, b) => a.date.localeCompare(b.date))); setModifie(true);
    setMsg(t('vacances.feriesAjoutes', { n: nouv.length, annee: anFerie }));
  }
  function ajouterFerie() {
    const nom = nomFerieNew.trim();
    if (!nom || !/^\d{4}-\d{2}-\d{2}$/.test(dateFerieNew)) return;
    if (feries.some(f => f.date === dateFerieNew)) { setMsg(t('vacances.ferieExisteDeja')); return; }
    setFeries(prev => [...prev, { nom, date: dateFerieNew }].sort((a, b) => a.date.localeCompare(b.date)));
    setNomFerieNew(''); setDateFerieNew(''); setModifie(true); setMsg('');
  }
  function supprimerFerie(date, nom) {
    confirmerSuppression(t('vacances.supprimerFerieTitre'), t('vacances.ferieRetireMsg', { nom }), () => {
      setFeries(fs => fs.filter(f => f.date !== date)); setModifie(true);
    });
  }

  async function enregistrer() {
    if (!calId) return;
    setBusy(true); setMsg('');
    try {
      const cfg = (await chargerVacancesConfig(calId)) || {};
      await sauvegarderVacancesConfig(calId, { ...cfg, zone, annee, periodes, feries });
      setModifie(false); setMsg(t('vacances.enregistreOk'));
    } catch (e) { setMsg(e?.message || t('messagerie.enregistrerImpossible')); }
    setBusy(false);
  }

  const ajoutFerieValide = nomFerieNew.trim() && /^\d{4}-\d{2}-\d{2}$/.test(dateFerieNew);

  return (
   <>
    <Feuille visible={visible} titre={t('famille.vacancesLabel')} emoji="🏖️" onFermer={onFermer}
      action={{ icone: '💾', onPress: enregistrer, disabled: busy || !modifie, label: t('commun.enregistrer') }}>

      {/* ── Vacances scolaires ── */}
      <SectionPliable titre={t('vacances.sectionScolaires')} emoji="🏖️" sousTitre={t('vacances.nPeriodes', { n: periodes.length })}
        ouvert={ouvert === 'vac'} onToggle={() => setOuvert(o => (o === 'vac' ? null : 'vac'))}>
        <Text style={s.intro}>{t('vacances.introScolaires')}</Text>

        <Text style={s.lbl}>{t('vacances.zone')}</Text>
        <View style={s.puces}>{ZONES.map(z => (
          <TouchableOpacity key={z} style={[s.puce, zone === z && s.puceOn]} onPress={() => setZone(z)}>
            <Text style={[s.puceTxt, zone === z && s.puceTxtOn]}>{z}</Text>
          </TouchableOpacity>))}</View>

        <Text style={s.lbl}>{t('vacances.anneeScolaire')}</Text>
        <View style={s.puces}>{annees.map(a => (
          <TouchableOpacity key={a} style={[s.puce, annee === a && s.puceOn]} onPress={() => setAnnee(a)}>
            <Text style={[s.puceTxt, annee === a && s.puceTxtOn]}>{a}</Text>
          </TouchableOpacity>))}</View>

        <TouchableOpacity style={s.importBtn} onPress={busy ? undefined : chargerVac} disabled={busy}>
          {busy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={s.importTxt}>{t('vacances.ajouterVacancesDe', { zone, annee })}</Text>}
        </TouchableOpacity>

        {periodes.length > 0 && <Text style={s.aideMini}>{t('vacances.aideSwipePeriode')}</Text>}
        {periodes.map((p, i) => {
          const k = cle(p);
          return (
            <LigneSwipe key={k} espacement={9} onSupprimer={() => supprimerVac(k, p.nom)}>
              <TouchableOpacity style={s.periode} activeOpacity={0.7} onPress={() => setFiche(i)}>
                <Text style={{ fontSize: 18 }}>{emojiVacances(p.nom)}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.nom} numberOfLines={1}>{p.nom}</Text>
                  <Text style={s.dates}>{fmtFR(p.debut)} → {fmtFR(p.fin)}</Text>
                </View>
                <Text style={s.chev}>›</Text>
              </TouchableOpacity>
            </LigneSwipe>
          );
        })}
      </SectionPliable>

      {/* ── Jours fériés ── */}
      <SectionPliable titre={t('vacances.sectionFeries')} emoji="🎉" sousTitre={t('vacances.nJours', { n: feries.length })}
        ouvert={ouvert === 'fer'} onToggle={() => setOuvert(o => (o === 'fer' ? null : 'fer'))}>
        <Text style={s.intro}>{t('vacances.introFeries')}</Text>

        <Text style={s.lbl}>{t('finances.annee')}</Text>
        <View style={s.puces}>{anneesFerie.map(a => (
          <TouchableOpacity key={a} style={[s.puce, anFerie === a && s.puceOn]} onPress={() => setAnFerie(a)}>
            <Text style={[s.puceTxt, anFerie === a && s.puceTxtOn]}>{a}</Text>
          </TouchableOpacity>))}</View>

        <TouchableOpacity style={s.importBtn} onPress={importerFeries}>
          <Text style={s.importTxt}>{t('vacances.ajouterFeries', { annee: anFerie })}</Text>
        </TouchableOpacity>

        {/* Ajout personnalisé */}
        <Text style={[s.lbl, { marginTop: 16 }]}>{t('vacances.ajouterFerie')}</Text>
        <TextInput style={s.input} value={nomFerieNew} onChangeText={setNomFerieNew}
          placeholder={t('vacances.phNomFerie')} placeholderTextColor={UI.faint} />
        <TouchableOpacity style={[s.champBtn, { marginTop: 8 }]} onPress={() => setPickFerie(true)}>
          <Text style={[s.champVal, !dateFerieNew && s.champVide]}>📅 {dateFerieNew ? fmtFR(dateFerieNew) : t('vacances.choisirDate')}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.ajoutBtn, !ajoutFerieValide && { opacity: 0.4 }]} disabled={!ajoutFerieValide} onPress={ajouterFerie}>
          <Text style={s.ajoutTxt}>＋ {t('commun.ajouter')}</Text>
        </TouchableOpacity>

        {feries.length > 0 && <Text style={[s.aideMini, { marginTop: 12 }]}>{t('vacances.aideSwipe')}</Text>}
        {feries.map(f => (
          <LigneSwipe key={f.date} espacement={8} onSupprimer={() => supprimerFerie(f.date, f.nom)}>
            <View style={s.periode}>
              <Text style={{ fontSize: 18 }}>🎉</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.nom} numberOfLines={1}>{f.nom}</Text>
                <Text style={s.dates}>{fmtFR(f.date)}</Text>
              </View>
            </View>
          </LigneSwipe>
        ))}
      </SectionPliable>

      {!!msg && <Text style={s.msg}>{msg}</Text>}
    </Feuille>

    {/* Sœurs (calques) : fiche de période + sélecteur de date du férié perso. */}
    <FeuilleFichePeriode
      visible={visible && fiche >= 0} periode={fiche >= 0 ? periodes[fiche] : null}
      onFermer={() => setFiche(-1)}
      onEnregistrer={(champs) => { setPeriodes(l => l.map((x, k) => (k === fiche ? { ...x, ...champs } : x)).sort((a, b) => a.debut.localeCompare(b.debut))); setModifie(true); setFiche(-1); }}
      onSupprimer={() => { const idx = fiche; setPeriodes(l => l.filter((_, k) => k !== idx)); setModifie(true); }} />
    <SelecteurDate visible={pickFerie} titre={t('vacances.dateFerieTitre')} valeur={dateFerieNew}
      onChange={setDateFerieNew} onFermer={() => setPickFerie(false)} />
   </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  intro: { fontSize: 12.5, color: UI.muted, lineHeight: 18, marginBottom: 6 },
  lbl:   { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  puces: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  puce:  { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  puceOn:{ backgroundColor: UI.accentTint, borderColor: UI.accent },
  puceTxt:{ fontSize: 12.5, color: UI.ink, fontWeight: '600' },
  puceTxtOn:{ color: UI.accent, fontWeight: '800' },
  importBtn: { backgroundColor: UI.accent, borderRadius: 11, paddingVertical: 11, alignItems: 'center', marginTop: 14 },
  importTxt: { color: '#fff', fontWeight: '800', fontSize: 13 },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  champBtn: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, borderWidth: 0.5, borderColor: UI.champBord },
  champVal: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  champVide: { color: UI.faint, fontWeight: '400' },
  ajoutBtn: { marginTop: 10, borderWidth: 1, borderColor: UI.accent, borderStyle: 'dashed', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  ajoutTxt: { color: UI.accent, fontWeight: '700', fontSize: 13 },
  aideMini: { fontSize: 11, color: UI.faint, marginTop: 10, marginBottom: 2 },
  periode: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderColor: UI.border, borderRadius: 12, backgroundColor: UI.card, padding: 11, marginBottom: 0 },
  nom:   { fontSize: 14, fontWeight: '700', color: UI.ink },
  dates: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  chev:  { fontSize: 18, color: UI.faint },
  msg:   { fontSize: 12.5, color: UI.accent, marginTop: 12, textAlign: 'center' },
});
